import { Helmet } from 'react-helmet-async';
import { Button } from "@/components/ui/button";
import { Timer, MessageCircle } from "lucide-react";
import { useEffect } from "react";

// Extend Window interface for GTM
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

export default function ObrigadoCompra() {
  
  // Track page view and conversion events on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && window.gtag) {
      const urlParams = new URLSearchParams(window.location.search);
      const orderId = urlParams.get('pedido');
      const totalValue = urlParams.get('valor');
      const origem = urlParams.get('origem') || 'app';
      const rota = urlParams.get('rota') || '/';
      const metodo = urlParams.get('metodo') || '';
      const campanha = urlParams.get('campanha') || '';

      // Page view event
      window.gtag('event', 'page_view', {
        page_title: 'Compra Confirmada',
        page_location: window.location.href,
        page_path: '/obrigado-compra'
      });

      // Thank you page conversion event
      window.gtag('event', 'purchase_thank_you', {
        event_category: origem === 'landing_page' ? 'landingpage' : 'app',
        event_label: origem === 'landing_page' 
          ? `landingpage-${campanha || rota.replace(/^\/+|\/+$/g, '').replace(/\//g, '-')}` 
          : 'app',
        purchase_source: origem,
        purchase_route: rota,
        payment_method: metodo
      });

      // Purchase confirmation with complete data
      if (orderId) {
        window.gtag('event', 'purchase_confirmation', {
          event_category: origem === 'landing_page' ? 'landingpage' : 'app',
          event_label: origem === 'landing_page' 
            ? `landingpage-${campanha || rota.replace(/^\/+|\/+$/g, '').replace(/\//g, '-')}` 
            : 'app',
          transaction_id: orderId,
          value: totalValue ? parseFloat(totalValue) : undefined,
          currency: 'BRL',
          payment_method: metodo,
          purchase_source: origem,
          purchase_route: rota,
          campaign_slug: campanha
        });

        console.log('GTM enhanced events sent on thank you page:', {
          origem,
          rota,
          metodo,
          campanha,
          transaction_id: orderId,
          value: totalValue
        });
      }
    }
  }, []);
  
  const handleWhatsAppClick = () => {
    const message = "Olá! Acabei de fazer uma compra e gostaria de acompanhar meu pedido.";
    const phone = "5511999999999"; // Número da empresa
    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <>
      <Helmet>
        <title>Compra Confirmada | AuraCode</title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full animate-in fade-in duration-700">
          <div className="bg-card border border-border rounded-2xl shadow-2xl p-8 md:p-12">
            <div className="text-center space-y-6">
              {/* Header */}
              <div className="space-y-2">
                <h1 className="text-3xl md:text-4xl font-bold text-foreground">
                  🎉 Tudo certo com sua compra!
                </h1>
                <p className="text-lg text-muted-foreground">
                  Sua compra foi concluída com sucesso e já estamos preparando seu acesso especial!
                </p>
              </div>

              {/* Main Message */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
                <p className="text-base text-foreground">
                  Em poucos instantes você receberá um <strong>e-mail</strong> e uma mensagem no seu <strong>WhatsApp</strong> com seu link exclusivo para acessar a plataforma.
                </p>
              </div>

              {/* Important Notice */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Timer className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                  <span className="font-semibold text-lg text-amber-800 dark:text-amber-200">
                    Importante:
                  </span>
                </div>
                <div className="text-base text-amber-800 dark:text-amber-200 space-y-2">
                  <p>Esse link é válido por <strong>24 horas</strong>.</p>
                  <p>Para garantir uma experiência tranquila, recomendamos acessar assim que recebê-lo. 😊</p>
                </div>
              </div>

              {/* Footer Message */}
              <div className="space-y-4 pt-2">
                <p className="text-lg text-foreground font-medium">
                  Estamos animados em ter você com a gente!
                </p>
                <p className="text-base text-muted-foreground">
                  Qualquer dúvida, estamos aqui para ajudar.
                </p>
              </div>

              {/* WhatsApp Button */}
              <div className="pt-4">
                <Button
                  onClick={handleWhatsAppClick}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  size="lg"
                >
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Abrir WhatsApp
                </Button>
              </div>

              {/* Optional: Back to Home Link */}
              <div className="pt-6 border-t border-border">
                <a 
                  href="/" 
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors underline"
                >
                  Voltar para a página inicial
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
